import {
  Component,
  OnInit,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectItem } from '@openng/optimus-ui/api';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { CONTACT_LIST_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import { FormComponent } from 'src/app/core/containers/form.component';
import { dynamicSort } from 'src/app/core/functions/common-functions';
import { TabService } from 'src/app/core/services/tab.service';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { Contact } from 'src/app/model/contacts/contact';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ContactListBuilderService } from 'src/app/services/builder/contact-list-builder.service';
import { ContactListTableService } from 'src/app/services/table/contact-list-table.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MemberCode } from 'src/app/model/contacts/member-code';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { ContactListMember } from 'src/app/model/contacts/contact-list-member';
import {
  AutoCompleteCompleteEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { InputText } from '@openng/optimus-ui/inputtext';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { Panel } from '@openng/optimus-ui/panel';
import { MemberCodesFormComponent } from '../../components/member-codes-form/member-codes-form.component';
import { TranslatePipe } from '@ngx-translate/core';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';

@Component({
  selector: 'app-contact-list-create-container',
  templateUrl: './contact-list-create-container.component.html',
  styleUrls: ['./contact-list-create-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ReactiveFormsModule,
    ButtonDirective,
    InputText,
    MultiSelect,
    AutoComplete,
    Panel,
    MemberCodesFormComponent,
    TranslatePipe,
    BlockableDiv,
  ],
})
export class ContactListCreateContainerComponent
  extends FormComponent
  implements OnInit
{
  protected _contactListTable = inject(ContactListTableService);
  protected _contactListApi = inject(ContactListApiService);
  protected _contactListBuilder = inject(ContactListBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _authApi = inject(AuthApiService);
  protected _contactApi = inject(ContactApiService);
  protected _tabService = inject(TabService);

  readonly embedded = input(false);
  readonly newContactCreatedEvent = output<any>();

  saving = false;
  allContacts: Contact[] = [];
  filteredContacts: SelectItem[] = [];
  displayDialog = false;
  addContactToListForm!: UntypedFormGroup;
  members: any[] = [];
  addContactsReady = false;
  canCreate = false;
  loaded = false;
  displayMemberCodesDialog: boolean = false;
  memberCodes: MemberCode[] = [];
  codesToDisplay: string = '';
  availableBusinessUnits: SelectItem[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this.addContactToListForm = this._fb.group({
      contact: [null, Validators.required],
    });

    this.initialize();
  }

  initialize() {
    this.loaded = true;
    this.members = [];

    this.availableBusinessUnits = this._authApi
      .getUserBusinessUnits()
      .map((bu) => {
        return { label: bu.name, value: bu.id };
      });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACT_LISTS.CREATE_CONTACT_LISTS,
    ]);

    if (this.embedded() === false) {
      // setup the bread crumb.
      const list$ = this._translateService.get('CONTACT.SCREEN.CONTACT_LIST');
      const create$ = this._translateService.get(
        'CONTACT.SCREEN.CONTACT_LIST_CREATE'
      );
      forkJoin([list$, create$])
        .pipe(take(1))
        .subscribe((messages) => {
          this.screenName = messages[1];
          this._tabService.updateActiveTabLabel(this.screenName);
        });
    }

    this.form = this._fb.group({
      name: [null, Validators.required],
      memberCodes: [null],
      businessUnits: [null, Validators.required],
    });

    this._contactApi
      .getContacts()
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.allContacts = response.sort(
            dynamicSort('firstName', 1)
          ) as Contact[];
          this.addContactsReady = true;
        },
      });
  }

  saveContactList() {
    this.saving = true;

    if (this.members && this.form) {
      const raw = this.form.getRawValue();
      const contactList: ContactList = {
        id: '',
        members: [],
        updaters: [],
        origMemberCodes: [],
        memberCodes: [],
        businessUnits: [],
        name: raw.name,
        additionalResourceListTypeId: raw.additionalResourceListTypeId,
        AdditionalResourceListType: raw.AdditionalResourceListType,
        ManagementFacilities: [],
        AdditionalResourceListFacilities: [],
      };
      const contactListInput = {
        id: contactList.id,
        name: contactList.name,
        memberCodes: this.memberCodes?.map((c) => ({
          id: c.id,
          code: c.code,
        })),
        members: this.members.map((m) => ({
          ContactListId: contactList.id,
          ContactId: m.contact.id,
          order: m.order,
          dirtyStatus: DirtyStatus.NEW,
        })),
        businessUnits: raw.businessUnits.map((bu: string) => {
          return {
            id: bu,
          };
        }),
      };

      this._contactListApi.createContactList(contactListInput).subscribe({
        next: (contactList) => {
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
          });
          if (this.embedded() === false) {
            this.form?.reset();
            this._contactListTable.setSelected(contactList);
            const oldTab = this._tabService.getActiveTab();
            const tab = this._tabService.buildNewTab(
              CONTACT_LIST_DETAIL_CONTAINER,
              true,
              undefined,
              contactList!.id
            );
            this._tabService.replaceTab(oldTab, tab);
          } else {
            this.newContactCreatedEvent.emit(contactList);
          }
          this.saving = false;
        },
        error: (error) => {
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CONTACT.MESSAGES.ERROR.CREATE_CONTACT_LIST',
          });
          console.error(error);
          this.saving = false;
        },
      });
    } else {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'CONTACT.MESSAGES.HEADERS.CONTACT_LIST_MISSING_LEVEL',
        bodyKey: 'CONTACT.MESSAGES.ERROR.CONTACT_LIST_MISSING_LEVEL_MSG',
      });
      this.saving = false;
    }
  }

  deleteMember(memberToRemove: ContactListMember) {
    const idx = this.members.findIndex(
      (m) => m.contact.id === memberToRemove.contact?.id
    );
    this.members.splice(idx, 1);
    this.members = [...this.members];
  }

  addContact() {
    const raw = this.addContactToListForm.getRawValue();
    const array = this.allContacts.filter((c) => c.id === raw.contact);
    if (array.length > 0) {
      this.members = [
        ...this.members,
        this._contactListBuilder.buildNewMember(
          '',
          array[0],
          this.members.length + 1
        ),
      ];
    }

    this.addContactToListForm.reset();
  }

  contactSearch($event: AutoCompleteCompleteEvent) {
    if ($event.query) {
      this.filteredContacts = this.allContacts
        .filter((c) =>
          (c.firstName + ' ' + c.lastName)
            .toLowerCase()
            .includes($event.query.toLowerCase())
        )
        .filter((c: Contact) => c.inactiveAdAccount === false)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }));
    } else {
      this.filteredContacts = this.allContacts.map((c) => ({
        label: c.firstName + ' ' + c.lastName,
        value: c.id,
      }));
    }
  }

  manageMemberCodes() {
    this.displayMemberCodesDialog = true;
  }

  onMemberCodesHidden() {
    this.displayMemberCodesDialog = false;
  }

  onMemberCodesUpdated($event: MemberCode[]) {
    this.displayMemberCodesDialog = false;
    this.memberCodes = $event;

    this.displayMemberCodes();
  }

  displayMemberCodes() {
    this.codesToDisplay = '';
    let assignedMemberCodes: string[] = [];
    this.memberCodes.forEach((c) => {
      assignedMemberCodes.push(c.code);
    });

    this.codesToDisplay = assignedMemberCodes.join(', ');
  }
}
